import { useCallback, useEffect, useState } from 'react';

export interface HomeFilters {
  city: string | null;
  tags: string[];
}

function parseTagsParam(value: string | null): string[] {
  if (!value) return [];
  return value
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean);
}

function readFilters(): HomeFilters {
  if (typeof window === 'undefined') return { city: null, tags: [] };
  const url = new URL(window.location.href);
  // Accept both `?tags=a,b` (current) and legacy `?tag=a` (single).
  // Legacy is only read when the new param is absent.
  const tagsParam = url.searchParams.get('tags');
  const legacy = url.searchParams.get('tag');
  const tags = tagsParam ? parseTagsParam(tagsParam) : legacy ? [legacy] : [];
  return {
    city: url.searchParams.get('city'),
    tags,
  };
}

/**
 * Keeps `?city=` and `?tags=` query params in sync with React state.
 *
 * - Initial render reads the URL (deep-links work).
 * - `pushState` for navigation so the page does not reload.
 * - `popstate` listener re-syncs state on back/forward.
 * - Anchor consumers can delegate to the mutators while keeping `<a href>`
 *   markup so right-click / middle-click "open in new tab" still works.
 */
export function useHomeFilters() {
  const [filters, setFilters] = useState<HomeFilters>(readFilters);

  useEffect(() => {
    const onPop = () => setFilters(readFilters());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const writeUrl = useCallback((next: HomeFilters) => {
    const url = new URL(window.location.href);
    if (next.city) url.searchParams.set('city', next.city);
    else url.searchParams.delete('city');
    if (next.tags.length > 0) url.searchParams.set('tags', next.tags.join(','));
    else url.searchParams.delete('tags');
    // Drop legacy param so URL stays canonical.
    url.searchParams.delete('tag');
    window.history.pushState({}, '', url);
    setFilters(next);
  }, []);

  const setCity = useCallback(
    (slug: string | null) => writeUrl({ ...readFilters(), city: slug }),
    [writeUrl],
  );

  const toggleTag = useCallback(
    (slug: string) => {
      const current = readFilters();
      const nextTags = current.tags.includes(slug)
        ? current.tags.filter((t) => t !== slug)
        : [...current.tags, slug];
      writeUrl({ ...current, tags: nextTags });
    },
    [writeUrl],
  );

  const removeTag = useCallback(
    (slug: string) => {
      const current = readFilters();
      writeUrl({ ...current, tags: current.tags.filter((t) => t !== slug) });
    },
    [writeUrl],
  );

  const clearAll = useCallback(
    () => writeUrl({ city: null, tags: [] }),
    [writeUrl],
  );

  return { filters, setCity, toggleTag, removeTag, clearAll };
}
